import { useLocation } from '@tanstack/react-router'
import { useEffect } from 'react'

/** The ring an arrived measure wears while the reader's eye finds it. */
const ARRIVED = ['ring-2', 'ring-accent', 'ring-offset-2', 'ring-offset-background']

/** How long the ring stays: long enough to be seen after the scroll settles. */
const ARRIVED_MS = 2400

/**
 * A dashboard opened at one measure (`#measure-<widget id>`): Mitra's
 * "Open in Analytics Studio" after a save names the widget the save added
 * (2026-10-20), so the reader lands on what they just kept and not at the
 * top of a long page. The measure is scrolled into view and ringed for a
 * moment; a table's column is found through the table that draws it. A hash
 * that names no measure on the page does nothing.
 *
 * Run once the dashboard has drawn: the anchor exists only then.
 */
export function useArrivedMeasure(drawn: boolean) {
  const hash = useLocation({ select: (location) => location.hash })
  useEffect(() => {
    if (!drawn || !hash.startsWith('measure-')) return
    const id = hash.slice('measure-'.length)
    const target =
      document.getElementById(hash) ??
      document.querySelector<HTMLElement>(`[data-measures~="${CSS.escape(id)}"]`)
    if (!target) return
    target.scrollIntoView({ block: 'center', behavior: 'smooth' })
    target.classList.add(...ARRIVED)
    target.setAttribute('data-arrived', 'true')
    const timer = setTimeout(() => {
      target.classList.remove(...ARRIVED)
      target.removeAttribute('data-arrived')
    }, ARRIVED_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [drawn, hash])
}
